/**
 * useInspect, the new UI's own data binding. Drives raw text → parsed → analysed
 * by calling the pure ENGINE functions directly (`parseEmail`, `analyzeEmail`).
 * No legacy hooks (useEmail / useFileLoader / useMboxSelection) and no React
 * context, just local state over the engine.
 *
 * Analysis is effect-driven so the active verification mode (Settings →
 * Verification, persisted to `emlx:verification-mode`) actually changes what the
 * engine does: the synchronous analysis lands immediately, then the async
 * sandbox + crypto-signature verdicts fold in when the mode opts into them.
 * Flipping the mode re-runs the pass over the already-parsed message.
 *
 * The hook holds TWO strings, and the difference between them is the whole
 * point: `draft` is the text a reader is typing (UTF-16 code units, shown in
 * the textarea) and `source` is the message under inspection (octets, one per
 * code unit, what every digest, size and export reads). They were one field
 * until 2026-08-28, so a paste reached the SHA-256 as its code units masked
 * with `& 0xff` — a byte sequence matching no file that could exist.
 * `inspect()` is the seam: it encodes the draft to UTF-8 exactly once.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { EmailAnalysis } from "../email-analysis";
import { parseEmail, type ParsedEmail } from "../email-parser";
import { useVerificationMode } from "../hooks/useVerificationMode";
import { byteStringLength, textToByteString } from "../utils/buffer";
import { modeAllowsCrypto, modeAllowsSandbox } from "../verification";
import { loadErrorMessage, type LoadError, type LoadErrorCode } from "./loadEmailFile";

/** Every failure the inspect surface can show: the file-loader codes plus the
 * hook's own three. Stable codes, localized at the display site (i18n err.*). */
export type InspectErrorCode =
  | LoadErrorCode
  | "paste-first"
  | "not-email"
  | "analysis-failed";

export interface InspectError {
  code: InspectErrorCode;
  /** Engine parse message ("not-email" only). Forensic English, by design. */
  detail?: string;
  /** The live byte cap that rejected the file ("too-large" only). */
  capBytes?: number;
}

/** Localize an inspect failure with the active t(). */
export function inspectErrorMessage(
  err: InspectError,
  t: (key: string) => string,
): string {
  switch (err.code) {
    case "paste-first":
      return t("err.pasteFirst");
    case "analysis-failed":
      return t("err.analysisFailed");
    case "not-email":
      return err.detail
        ? t("err.notEmailDetail").replace("{detail}", err.detail)
        : t("err.notEmail");
    default:
      return loadErrorMessage({ code: err.code, capBytes: err.capBytes }, t);
  }
}

export interface InspectState {
  /**
   * The message under inspection, as a BYTE STRING: one octet per UTF-16 code
   * unit, so `charCodeAt(i)` is octet `i`. Everything that has to describe the
   * message as an artifact reads this and nothing else — the envelope's
   * SHA-256 and size, the evidence bundle's `raw/*.eml`, the library entry's
   * id. Written only by {@link InspectState.inspect}.
   */
  source: string;
  /**
   * What the reader is typing into the home textarea: a normal JS string,
   * where a code unit is UTF-16 and "ü" is one unit rather than two octets.
   *
   * Held apart from `source` on purpose. It used to be the same field, which
   * is what let a paste reach the digest as a code-unit string masked with
   * `& 0xff` — a byte sequence matching no file anywhere. `inspect()` encodes
   * the draft to UTF-8 once; the draft itself keeps showing what was typed, so
   * the reader never faces mojibake in a field they are still editing and a
   * second press cannot encode an already-encoded string.
   */
  draft: string;
  setDraft: (s: string) => void;
  parsed: ParsedEmail | null;
  analysis: EmailAnalysis | null;
  error: InspectError | null;
  pending: boolean;
  /**
   * Inspect a message. With an argument the caller supplies BYTES already (the
   * file loader, the `#import=` handoff, a mailbox block) and they are used
   * verbatim; with none, the draft is encoded to UTF-8 first. Nothing here
   * re-encodes a byte string, which would corrupt exactly the non-UTF-8
   * messages the invariant exists for.
   */
  inspect: (byteSource?: string) => void;
  /** Surface a file-load failure (too large, unreadable, etc.) on the home. */
  fail: (err: LoadError) => void;
  reset: () => void;
}

/** Strip a single leading mbox `From ` envelope line if present. */
function stripMbox(raw: string): string {
  const s = raw.replace(/^\uFEFF/, "");
  if (/^From /.test(s.trimStart())) {
    const nl = s.indexOf("\n");
    if (nl !== -1) return s.slice(nl + 1);
  }
  return s;
}

export function useInspect(): InspectState {
  const [source, setSource] = useState("");
  const [draft, setDraft] = useState("");
  const [parsed, setParsed] = useState<ParsedEmail | null>(null);
  const [analysis, setAnalysis] = useState<EmailAnalysis | null>(null);
  const [error, setError] = useState<InspectError | null>(null);
  const [pending, setPending] = useState(false);
  const { mode } = useVerificationMode();

  // The exact body that produced `parsed` (without the mbox envelope line).
  // Held in a ref so the analysis effect can read it without taking a
  // dependency on the live `source` (which changes on every textarea keystroke
  // and must NOT re-trigger analysis against a stale parse).
  const bodyRef = useRef("");

  const inspect = useCallback(
    (byteSource?: string) => {
      // The ONE place pasted text is given an encoding, and the reason the
      // draft is a separate field: `raw` is octets from here down, so calling
      // inspect() twice on the same draft produces the same bytes rather than
      // encoding its own output.
      const raw = byteSource ?? textToByteString(draft);
      const body = stripMbox(raw);
      if (!body.trim()) {
        setError({ code: "paste-first" });
        return;
      }
      let result: ParsedEmail;
      try {
        result = parseEmail(body);
      } catch (e) {
        setError({
          code: "not-email",
          detail: e instanceof Error && e.message ? e.message : undefined,
        });
        setParsed(null);
        setAnalysis(null);
        setPending(false);
        return;
      }
      bodyRef.current = body;
      // The draft is deliberately NOT touched: a reader mid-edit keeps their
      // own text, and a file load has no business rewriting a field they typed.
      setSource(raw);
      setParsed(result);
      // CLEAR THE OLD ANALYSIS IN THE SAME COMMIT as the new parse.
      //
      // Analysis is async. Without this, every consumer saw {parsed: B,
      // analysis: A} for the whole window between opening a second message and
      // its analysis landing: message B's envelope under message A's verdict,
      // A's authentication chips, A's indicators. In a tool whose entire job is
      // saying which message is which, that is the worst possible wrong state,
      // and it persisted indefinitely if B's analysis then failed.
      //
      // `parsed && analysis` is what gates the report, so nulling it here means
      // the reader gets the reassembling placeholder instead of a false pairing.
      setAnalysis(null);
      setError(null);
    },
    [draft],
  );

  // Analysis pass. Re-runs whenever the parsed message OR the verification mode
  // changes. parse-only / dns-enabled land the synchronous shape only; crypto-
  // enabled folds DKIM / S-MIME / PGP verdicts onto the signatures; sandboxed
  // attaches the bounded-worker findings. None of these touch the network: the
  // live DNS lookups are a separate, explicit, on-demand affordance.
  useEffect(() => {
    if (!parsed) {
      setAnalysis(null);
      // A parse failure cancels the previous effect (suppressing its finally)
      // and then returns here, so without this the spinner never stopped.
      setPending(false);
      return;
    }
    let cancelled = false;
    const body = bodyRef.current;
    const sourceBytes = byteStringLength(body);
    setPending(true);
    void (async () => {
      try {
        const { analyzeEmail } = await import("../email-analysis");
        if (cancelled) return;
        let next = analyzeEmail(parsed, { sourceBytes, verificationMode: mode });

        // Sandbox pass: bounded Web Worker structural inspection. Only fires in
        // `sandboxed` mode; folds the result onto `analysis.sandbox`.
        if (modeAllowsSandbox(mode)) {
          const { runSandboxPipeline } = await import("../verification/sandbox-pipeline");
          if (cancelled) return;
          const sandbox = await runSandboxPipeline(parsed);
          if (cancelled) return;
          next = { ...next, sandbox };
        }

        // Crypto pass: on-device DKIM body-hash + S/MIME + PGP signature math.
        // No network by construction, so a DKIM signature whose public key is
        // only in DNS stays unverified rather than silently resolving one.
        // Skipped entirely when the mode forbids it or the message carries
        // nothing to verify, which is what keeps the decoders out of the
        // eager bundle for every other message.
        if (
          modeAllowsCrypto(mode) &&
          (next.dkimSignatures.length > 0 || next.securityParts.length > 0)
        ) {
          const { verifyCryptoForAnalysis } =
            await import("../derivations/crypto-verify");
          if (cancelled) return;
          const results = await verifyCryptoForAnalysis(
            parsed,
            body,
            next.dkimSignatures,
            next.securityParts,
            { verificationMode: mode, parseCrypto: true },
          );
          if (cancelled) return;
          if (results.dkim.length > 0 || results.secureParts.length > 0) {
            const dkimSignatures = next.dkimSignatures.map((sig, i) => {
              const verdict = results.dkim[i];
              return verdict ? { ...sig, cryptoVerify: verdict } : sig;
            });
            const securityParts = next.securityParts.map((p, i) => {
              const verdict = results.secureParts[i];
              return verdict ? { ...p, cryptoVerify: verdict } : p;
            });
            next = {
              ...next,
              dkimSignatures,
              securityParts,
              layers: {
                ...next.layers,
                authentication: {
                  ...next.layers.authentication,
                  dkimSignatures,
                },
                crypto: { ...next.layers.crypto, secureParts: securityParts },
              },
            };
          }
        }

        if (cancelled) return;
        setAnalysis(next);
        // A run that succeeds retires the previous run's failure notice.
        setError((e) => (e?.code === "analysis-failed" ? null : e));
      } catch {
        if (!cancelled) setError({ code: "analysis-failed" });
      } finally {
        if (!cancelled) setPending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [parsed, mode]);

  const fail = useCallback((err: LoadError) => {
    setError({ code: err.code, capBytes: err.capBytes });
    setParsed(null);
    setAnalysis(null);
    setPending(false);
  }, []);

  const reset = useCallback(() => {
    bodyRef.current = "";
    setSource("");
    setDraft("");
    setParsed(null);
    setAnalysis(null);
    setError(null);
    setPending(false);
  }, []);

  return {
    source,
    draft,
    setDraft,
    parsed,
    analysis,
    error,
    pending,
    inspect,
    fail,
    reset,
  };
}
